// Global packages and components
import { clsx } from 'clsx'
import { Link } from '@remix-run/react'
import { BannerImage, Button, Slider } from '~/components'
import { getLinkType } from '~/utils'
import { ArrowRightIcon } from '@heroicons/react/24/outline'

// Types
import type {
  SanityButtonProps,
  SanityCustomImage,
  SanityLinkObject,
} from '~/types'

type column = {
  bannerType: string
  title: string
  textWhite: boolean
  textSize: boolean
  titlePosition?: 'top' | 'bottom'
  hideHover: boolean
  hideOverlay: boolean
  image: SanityCustomImage
  link: SanityLinkObject
  button: SanityButtonProps
}

interface BlockCategoryGridProps {
  columns: column[]
}

// Main export
export default function BlockBannerColumns({
  columns,
}: BlockCategoryGridProps) {
  return (
    <div className="container">
      <Slider
        className="mb-12 h-full"
        options={{
          pagination: true,
          arrows: true,
          perPage: 1,
          gap: '1rem',
          breakpoints: {
            768: {
              perPage: columns?.length === 4 ? 2 : columns?.length,
              gap: '2rem',
            },
            1024: {
              perPage: columns?.length === 4 ? 4 : columns?.length,
              gap: '2rem',
            },
          },
        }}
      >
        {columns &&
          columns.map((column, i) => {
            const link = getLinkType({ ...column.link })

            if (!column?.image) return null

            return link ? (
              <Link
                to={link}
                className="group relative block aspect-square h-full overflow-hidden rounded-2xl"
                key={i}
              >
                <CardInner {...column} />
              </Link>
            ) : (
              <div
                className="relative aspect-square overflow-hidden rounded-2xl"
                key={i}
              >
                <CardInner {...column} />
              </div>
            )
          })}
      </Slider>
    </div>
  )
}

const CardInner = ({
  bannerType,
  title,
  textWhite,
  textSize,
  titlePosition,
  image,
  hideHover,
  hideOverlay,
  button,
}: column) => (
  <>
    <div
      className={clsx(
        'absolute left-0 top-0 z-[2] flex h-full w-full flex-col items-start px-4 py-3 text-white transition-colors duration-300 md:py-5',
        {
          'group-hover:text-vibrant-green': !hideHover,
          'justify-end': titlePosition !== 'top',
          'justify-between': titlePosition === 'top',
        }
      )}
    >
      <div className="flex w-full items-center justify-between gap-3">
        {title && bannerType == 'imageWithHtml' && (
          <h4
            className={clsx(
              'mb-0 text-left font-semibold',
              textSize ? 'text-2xl' : 'text-xl',
              {
                'opacity-0 transition-all duration-300 group-hover:text-vibrant-green group-hover:opacity-100':
                  !hideHover,
                'text-vibrant-green': hideHover && !textWhite,
                'text-white': textWhite,
              }
            )}
          >
            {title}
          </h4>
        )}
        {bannerType == 'imageWithHtml' && !button?.title && (
          <div className="ml-auto hidden size-7 items-center justify-center rounded-full bg-vibrant-green md:flex">
            <ArrowRightIcon className="size-4 text-forest-green" />
          </div>
        )}
      </div>
      {bannerType == 'imageWithHtml' && button?.title && (
        <div className="mt-3">
          <Button {...button} />
        </div>
      )}
    </div>
    <div
      className={clsx('absolute left-0 top-0 size-full', {
        'transition-transform group-hover:scale-105': !hideHover,
      })}
    >
      {image?.asset && (
        <BannerImage
          image={image}
          absolute={false}
          size={768}
          hideOverlay={hideOverlay}
        />
      )}
    </div>
  </>
)
